import {
  AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type AxiosInstance,
} from 'axios';
import type { AuthStoreAdapter, AuthFailureDetails } from '../types/auth';
import type { ClientState } from '../types/state';
import type { TokenRefreshConfig } from '../types/config';
import { ErrorCategory } from '../types/retry';
import { classifyError, getRetryStrategyForError } from '../classification';
import { sleep } from '../utils';

/**
 * Extended request config with retry flags.
 */
interface ExtendedRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryOn403?: boolean;
  /** Flag to prevent retry loop from re-entering */
  _isRetryingColdStart?: boolean;
  /** Current attempt number in retry loop (for logging/telemetry only) */
  _coldStartRetryCount?: number;
}

/**
 * Dependencies for response interceptor.
 */
export interface ResponseInterceptorDependencies {
  /** Axios instance for making requests */
  authenticatedInstance: AxiosInstance;

  /** Auth store for getting tokens */
  authStore: AuthStoreAdapter;

  /** Client state */
  state: ClientState;

  /** Token refresh configuration */
  config: TokenRefreshConfig;

  /** Function to handle token refresh */
  handleTokenRefresh: () => Promise<string>;

  /** Callback for auth failures (logout) */
  onAuthFailure: (details: AuthFailureDetails) => void;

  /** Callback for network errors (retry UI) */
  onNetworkError: (message: string, retryQueue: () => Promise<void>, isColdStart: boolean) => void;

  /** Function to retry all queued requests */
  retryQueuedRequests: () => Promise<void>;
}

/**
 * Response interceptor success handler.
 * Simply returns the response unchanged.
 */
export function responseInterceptorSuccessHandler(
  response: AxiosResponse
): AxiosResponse {
  return response;
}

/**
 * Response interceptor error handler.
 *
 * Handles various error scenarios:
 * 1. 401 Unauthorized → Token refresh and retry
 * 2. 403 Forbidden → Permission refresh (ONE retry) and retry
 * 3. Cold start/network errors → Exponential backoff retries
 *
 * @param deps Dependencies
 * @returns Error handler function for axios response interceptor
 */
export function createResponseInterceptorErrorHandler(
  deps: ResponseInterceptorDependencies
) {
  const {
    authenticatedInstance,
    authStore,
    state,
    config,
    handleTokenRefresh,
    onAuthFailure,
    onNetworkError,
    retryQueuedRequests,
  } = deps;

  return async (error: AxiosError): Promise<AxiosResponse> => {
    const originalRequest = error.config as ExtendedRequestConfig;

    // Handle 401 unauthorized responses
    // Skip retry for logout endpoints - if logout fails with 401, don't try to refresh and retry
    const isLogoutRequest = originalRequest.url?.includes('/auth/logout') === true;
    if (error.response?.status === 401 && !originalRequest._retry && !isLogoutRequest) {
      originalRequest._retry = true;

      const refreshToken = authStore.getRefreshToken();

      if (!refreshToken) {
        onAuthFailure({
          error: "No refresh token available",
          noRefreshToken: true,
          timestamp: new Date().toISOString(),
        });
        return Promise.reject(error);
      }

      try {
        // Queue this request if refresh is already in progress
        if (state.refreshPromise) {
          return new Promise<AxiosResponse>((resolve, reject) => {
            state.requestQueue.push({
              resolve,
              reject,
              config: originalRequest,
            });
          });
        }

        // Start token refresh
        const newToken = await handleTokenRefresh();

        // Retry original request with new token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return authenticatedInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden - attempt token refresh for updated permissions
    //
    // Rationale for ONE retry (configured in config.retryStrategies.permissionRefresh):
    // - Admin grants permission while user has active session
    // - User makes request → 403 (old token lacks permission)
    // - Refresh token → new token includes permission → retry succeeds
    // - If still 403 after refresh = genuine authorization failure (don't loop)
    //
    // This provides seamless UX for dynamic permission grants without infinite loops.
    // Skip retry for logout endpoints - no point refreshing permissions for logout
    if (error.response?.status === 403 && !originalRequest._retryOn403 && !isLogoutRequest) {
      originalRequest._retryOn403 = true;

      console.log(
        "🔐 403 Forbidden - attempting token refresh for updated permissions"
      );

      try {
        // Attempt token refresh (might have new permissions)
        const newToken = await handleTokenRefresh();

        // Retry original request with new token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        const response = await authenticatedInstance(originalRequest);

        // Success! Dispatch event for logging/monitoring
        console.log(
          "✅ Request succeeded after permission refresh (403 → 200)",
          originalRequest.url
        );

        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("auth:permission-refreshed", {
              detail: {
                endpoint: originalRequest.url,
                timestamp: Date.now(),
              },
            })
          );
        }

        return response;
      } catch (refreshError) {
        // Still 403 after refresh - genuine authorization failure
        if (
          refreshError instanceof AxiosError &&
          refreshError.response?.status === 403
        ) {
          console.log(
            "❌ Still forbidden after token refresh - genuine authorization failure"
          );
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle cold start and network errors - retry with exponential backoff
    // This applies to ALL authenticated requests, not just token refresh
    // Skip retry for logout endpoints - logout failures shouldn't trigger retry loops
    const errorCategory = classifyError(error, config.customErrorClassifier);
    const retryStrategy = getRetryStrategyForError(error, config);

    // Only retry if this is a cold start or network error AND we haven't already entered retry loop
    if (
      (errorCategory === ErrorCategory.ColdStart || errorCategory === ErrorCategory.NetworkError) &&
      retryStrategy.enabled &&
      !originalRequest._isRetryingColdStart &&
      !isLogoutRequest
    ) {
      // Set flag to prevent re-entry into retry loop
      originalRequest._isRetryingColdStart = true;

      console.log(
        `🥶 Cold start/network error detected on ${originalRequest.url} (${errorCategory})`,
        error.code || error.message
      );

      // Retry loop with exponential backoff
      for (let attempt = 1; attempt <= retryStrategy.maxAttempts; attempt++) {
        // Track attempt for logging/telemetry (not for control flow)
        originalRequest._coldStartRetryCount = attempt;

        const delay =
          retryStrategy.delays[attempt - 1] ||
          retryStrategy.delays[retryStrategy.delays.length - 1] ||
          1000;

        console.log(
          `🔄 Retrying ${originalRequest.url} (${errorCategory}, attempt ${attempt}/${retryStrategy.maxAttempts}), waiting ${delay}ms...`
        );

        // Telemetry: retry attempt
        if (config.telemetry?.enabled && config.telemetry.onRetryAttempt) {
          config.telemetry.onRetryAttempt({
            errorCategory,
            attemptNumber: attempt,
            maxAttempts: retryStrategy.maxAttempts,
            delayMs: delay,
            label: retryStrategy.telemetryLabel,
            timestamp: new Date().toISOString(),
          });
        }

        await sleep(delay);

        // Show network error dialog after first retry fails (if configured)
        // Use timestamp-based deduplication to prevent race conditions from concurrent requests
        const now = Date.now();
        const DIALOG_DEBOUNCE_MS = 1000; // Only show dialog once per second
        const shouldShowDialog =
          retryStrategy.shouldShowRetryUI &&
          retryStrategy.showMessageAfterFirstRetry &&
          attempt === 1 &&
          (!state.coldStartDialogShown ||
            (state.coldStartDialogShownAt &&
              now - state.coldStartDialogShownAt > DIALOG_DEBOUNCE_MS));

        if (shouldShowDialog) {
          state.coldStartDialogShown = true;
          state.coldStartDialogShownAt = now;
          const userMessage = retryStrategy.userMessage.replace(
            "{{attempts}}",
            (attempt + 1).toString()
          );

          console.log(`🚨 Showing cold start dialog to user after first retry failed`);

          // Trigger network error UI callback
          const isColdStart = errorCategory === ErrorCategory.ColdStart;
          if (onNetworkError) {
            onNetworkError(userMessage, retryQueuedRequests, isColdStart);
          }
        }

        // Retry the request
        try {
          const response = await authenticatedInstance(originalRequest);
          console.log(
            `✅ Request succeeded on attempt ${attempt}/${retryStrategy.maxAttempts} for ${originalRequest.url}`
          );

          // Reset cold start dialog flag on successful retry
          if (state.coldStartDialogShown && attempt > 1) {
            state.coldStartDialogShown = false;
            state.coldStartDialogShownAt = null;
            console.log('✅ Cold start resolved - dialog flag reset');
          }

          // Telemetry: retry success
          if (
            attempt > 1 &&
            config.telemetry?.enabled &&
            config.telemetry.onRetrySuccess
          ) {
            config.telemetry.onRetrySuccess({
              errorCategory,
              successfulAttempt: attempt,
              totalAttempts: attempt,
              label: retryStrategy.telemetryLabel,
              timestamp: new Date().toISOString(),
            });
          }

          return response;
        } catch (retryError) {
          // If this is the last attempt, handle failure
          if (attempt >= retryStrategy.maxAttempts) {
            console.error(
              `❌ Request failed after ${attempt} attempts (${errorCategory}) for ${originalRequest.url}`,
              (retryError as AxiosError).code || (retryError as AxiosError).message
            );

            // Telemetry: retry exhausted
            if (config.telemetry?.enabled && config.telemetry.onRetryExhausted) {
              config.telemetry.onRetryExhausted({
                errorCategory,
                attemptsMade: attempt,
                error: (retryError as AxiosError).message,
                label: retryStrategy.telemetryLabel,
                timestamp: new Date().toISOString(),
              });
            }

            // If dialog wasn't shown yet, show it now (after all retries exhausted)
            const now = Date.now();
            const DIALOG_DEBOUNCE_MS = 1000;
            const shouldShowDialogNow =
              retryStrategy.shouldShowRetryUI &&
              (!state.coldStartDialogShown ||
                (state.coldStartDialogShownAt &&
                  now - state.coldStartDialogShownAt > DIALOG_DEBOUNCE_MS));

            if (shouldShowDialogNow) {
              state.coldStartDialogShown = true;
              state.coldStartDialogShownAt = now;
              const userMessage = retryStrategy.userMessage.replace(
                "{{attempts}}",
                attempt.toString()
              );

              console.log(`🚨 Showing cold start dialog after all retries exhausted`);

              const isColdStart = errorCategory === ErrorCategory.ColdStart;
              if (onNetworkError) {
                onNetworkError(userMessage, retryQueuedRequests, isColdStart);
              }
            }

            // Queue this request for manual retry
            return new Promise<AxiosResponse>((resolve, reject) => {
              state.failedRequestsQueue.push({
                resolve,
                reject,
                config: originalRequest,
              });
              console.log(
                `📥 Queued failed request for manual retry: ${originalRequest.url}`
              );
            });
          }

          // Continue to next retry attempt
          console.log(
            `⚠️ Attempt ${attempt}/${retryStrategy.maxAttempts} failed for ${originalRequest.url}, will retry...`
          );
        }
      }
    }

    return Promise.reject(error);
  };
}
